import { useAuth } from '@/auth/useAuth';
import { useReadOnly } from '@/auth/useViewingAs';
import { STATUS_LABELS, isWritable } from '@/lib/organization';

/**
 * Whether the person may change the pages (P22): not while staff are
 * viewing, and not while the organization's status forbids writes. Read as
 * `useDocumentsWritable` reads it, and says why in one sentence, because the
 * list's and the editor's alerts name the reason in both cases (PB14).
 */
export function usePagesWritable(): {
  writable: boolean;
  viewing: boolean;
  statusForbids: boolean;
  /** The read-only alert's sentence; null when the person may write. */
  readOnlyReason: string | null;
} {
  const { user } = useAuth();
  const organization = user?.organizations.find((o) => o.id === user.organizationId);
  const viewing = useReadOnly();
  const statusForbids = !isWritable(organization?.status);
  const statusWord = (
    STATUS_LABELS[organization?.status ?? ''] ?? 'not active'
  ).toLowerCase();
  let readOnlyReason: string | null = null;
  if (statusForbids) {
    readOnlyReason = `This organization is ${statusWord}, so its pages cannot be changed. Contact Activist Technologies to discuss it.`;
  } else if (viewing) {
    readOnlyReason = 'You are viewing this organization, so its pages cannot be changed.';
  }
  return {
    writable: !statusForbids && !viewing,
    viewing,
    statusForbids,
    readOnlyReason,
  };
}
